import { ElementType, HTMLAttributes, ReactNode } from 'react';
import styles from './Typography.module.css';

// The Symphony type scale as a component. The size/weight/tracking live on the
// global `.sym-h1`…`.sym-p3` classes (styles/typography.css, the single source of
// truth); this wrapper adds the MUI-style layout knobs — color, alignment,
// truncation, bottom gutter — and lets the caller override the rendered element.
//
// `heading-02` is the one DS role with no app-facing `.sym-h*` alias — the legacy
// scale jumps 20→16 and has no 18/500 step — so it maps straight onto the generated
// role class `.sym-type-heading-02`.
export type TypographyVariant = 'display' | 'h1' | 'h2' | 'h3' | 'h4' | 'heading-02' | 'p1' | 'p2' | 'p3';
export type TypographyColor =
  | 'primary'
  | 'strong'
  | 'default'
  | 'secondary'
  | 'muted'
  | 'neutral'
  | 'neutralStrong'
  | 'accent'
  | 'inherit';

// Sensible default element per variant — overridable via `component`.
const VARIANT_ELEMENT: Record<TypographyVariant, ElementType> = {
  display: 'h1',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  'heading-02': 'h3',
  p1: 'p',
  p2: 'p',
  p3: 'p',
};

// Most variants resolve to `.sym-<variant>`; the role-named ones point at their
// generated `.sym-type-<role>` class directly.
const VARIANT_CLASS: Partial<Record<TypographyVariant, string>> = {
  'heading-02': 'sym-type-heading-02',
};

export interface TypographyProps extends Omit<HTMLAttributes<HTMLElement>, 'color'> {
  variant?: TypographyVariant;
  color?: TypographyColor;
  align?: 'start' | 'center' | 'end';
  /** Clamp to one line with an ellipsis (MUI `noWrap`). */
  noWrap?: boolean;
  /** Adds bottom margin so stacked headings/paragraphs breathe (MUI `gutterBottom`). */
  gutterBottom?: boolean;
  /** Render as a different element (e.g. a <span> label, a <label>). */
  component?: ElementType;
  children?: ReactNode;
}

export function Typography({
  variant = 'p2',
  color = 'default',
  align,
  noWrap,
  gutterBottom,
  component,
  className,
  children,
  ...rest
}: TypographyProps) {
  const Component = component ?? VARIANT_ELEMENT[variant];
  const classes = [
    VARIANT_CLASS[variant] ?? `sym-${variant}`,
    styles.root,
    styles[`color-${color}`],
    align && styles[`align-${align}`],
    noWrap && styles.noWrap,
    gutterBottom && styles.gutterBottom,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Component className={classes} {...rest}>
      {children}
    </Component>
  );
}
